/**
 * Where a signed-out visitor was going, kept across the sign-in that interrupts them.
 *
 * Deep links arrive as page loads: someone opens `/orders/{id}/manage` from a notification mail or
 * a Slack message, the route guard sends a signed-out visitor to `/`, and the sign-in that follows
 * used to land on `/translations` with the link forgotten. The path is recorded once, at
 * bootstrap, before the router runs any guard -- not in each guard, and not in the root route's
 * `beforeLoad`:
 *
 * - Some fifty guards redirect to `/` with nothing attached. Threading a parameter through each,
 *   and through the chains some of them form (`/orders/{id}/manage` -> `/orders` -> `/`), is a
 *   change every one of them would have to get right, now and in every route added later.
 * - The root `beforeLoad` runs again for each hop of such a chain, so the first hop's target is
 *   overwritten by the second's. It also runs for link preloads, which are not navigations.
 *
 * Only a page load is recorded. A session that ends while the app is open, followed by moving
 * around inside it, keeps today's behaviour; that is not the case this exists for.
 *
 * sessionStorage, because the intent belongs to this tab: a link opened in a new tab carries its
 * own, and the SSO round trip (mp-auth -> identity provider -> `/login/success`) is a sequence of
 * top-level navigations in the same tab, which sessionStorage survives.
 *
 * Not in `SESSION_STORAGE_KEYS`: it is written only while nobody is signed in and removed by the
 * sign-in that reads it, so it never belongs to an account a sign-out would have to tear down --
 * and `logout()` clears sessionStorage wholesale regardless.
 */
const RETURN_PATH_KEY = "auth.returnPath"

/**
 * Routes reached without signing in, or that are the sign-in itself.
 *
 * Recording one would send someone back to the login form, or to a terms page, after they signed
 * in. `/login` covers `/login/success`, whose load is the SSO return and must not replace the path
 * recorded before the visitor left for the identity provider; `/marketplace` is the same for the
 * Azure Marketplace hand-off.
 */
const PUBLIC_PATHS = [
  "/sign-up",
  "/reset-password",
  "/login",
  "/legal",
  "/marketplace",
  "/orders/anonymous",
] as const

const isPublicPath = (pathname: string) =>
  pathname === "/" ||
  PUBLIC_PATHS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )

type PageLocation = Pick<Location, "pathname" | "search" | "hash">

/** The location to come back to after signing in, or null for a page that is not worth it. */
export const returnPathFor = ({ pathname, search, hash }: PageLocation) =>
  isPublicPath(pathname) ? null : `${pathname}${search}${hash}`

/**
 * `path` as a location inside this app, or null when it is not one.
 *
 * Read back from storage, so it is checked rather than trusted. `//host` and `/\host` are
 * absolute URLs to a browser, hence the refusal before resolving; the origin comparison catches
 * anything else that resolves somewhere other than here.
 */
export const toInAppPath = (path: string, origin: string): string | null => {
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) {
    return null
  }
  let url: URL
  try {
    url = new URL(path, origin)
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * Records the page being loaded, if the visitor is signed out and the page is not public.
 *
 * `isAuthenticated` is the auth store's persisted flag -- the value every route guard reads -- so
 * this records exactly when a guard is about to send the visitor to the login form.
 */
export const rememberReturnPathOnLoad = (
  { isAuthenticated }: { isAuthenticated: boolean },
  location: PageLocation = window.location,
  storage: Pick<Storage, "setItem"> = sessionStorage
) => {
  if (isAuthenticated) return
  const path = returnPathFor(location)
  if (!path) return
  try {
    storage.setItem(RETURN_PATH_KEY, path)
  } catch {
    // Storage refused (private mode, quota): the link is forgotten, as it was before this existed.
  }
}

/** The recorded path, once: reading it removes it, so a later sign-in in this tab starts fresh. */
export const takeReturnPath = (
  origin: string = window.location.origin,
  storage: Pick<Storage, "getItem" | "removeItem"> = sessionStorage
): string | null => {
  let stored: string | null
  try {
    stored = storage.getItem(RETURN_PATH_KEY)
    storage.removeItem(RETURN_PATH_KEY)
  } catch {
    return null
  }
  return stored ? toInAppPath(stored, origin) : null
}
